import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Eye, Activity, Clock, TrendingUp, Users, MousePointer, Calendar, Globe } from 'lucide-react';
import { quotesApi } from '../services/api';
import { useToast } from '../hooks/use-toast';

const QuoteTracking = ({ quoteId, quotesData, trigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchTrackingData = async () => {
    if (!quoteId) return;
    
    setLoading(true);
    try {
      const response = await quotesApi.getTracking(quoteId);
      setTrackingData(response.data);
      try {
        const sres = await quotesApi.getSessions(quoteId);
        setSessions(sres.data?.sessions || []);
      } catch (_) { setSessions([]); }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Tracking-Daten konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && quoteId) {
      fetchTrackingData();
    }
  }, [isOpen, quoteId]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Nie';
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'view': return <Eye className="w-4 h-4" />;
      case 'download': return <TrendingUp className="w-4 h-4" />;
      case 'accept': return <Activity className="w-4 h-4 text-green-600" />;
      case 'reject': return <Activity className="w-4 h-4 text-red-600" />;
      case 'negotiate': return <Activity className="w-4 h-4 text-orange-600" />;
      default: return <MousePointer className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'view': return 'bg-blue-100 text-blue-800';
      case 'download': return 'bg-purple-100 text-purple-800';
      case 'accept': return 'bg-green-100 text-green-800';
      case 'reject': return 'bg-red-100 text-red-800';
      case 'negotiate': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const fmtDuration = (sec) => {
    if (!sec || sec < 1) return '< 1 Min.';
    const m = Math.floor(sec / 60), s2 = sec % 60;
    return m > 0 ? `${m} Min. ${s2} Sek.` : `${s2} Sek.`;
  };
  const EVENT_LABELS = {
    section_open: '📖 Sektion geöffnet',
    optional_on: '✅ Option gewählt',
    optional_off: '⬜ Option abgewählt',
    pdf: '🖨️ PDF/Druck',
    accepted: '🎉 Angebot angenommen',
    question: '❓ Frage gestellt',
    feedback: '💬 Feedback gegeben',
  };

  // Quick stats from quote data (if available)
  const quickStats = quotesData ? {
    views: quotesData.views || 0,
    lastViewed: quotesData.lastViewed,
  } : null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {quickStats ? (
              <span className="text-xs">
                {quickStats.views > 0 ? `${quickStats.views}x` : '0x'}
              </span>
            ) : (
              'Tracking'
            )}
          </Button>
        )}
      </DialogTrigger>
      
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Angebots-Tracking & Analytics
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Lade Tracking-Daten...</p>
          </div>
        ) : trackingData ? (
          <div className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-600">Gesamtaufrufe</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {trackingData.total_views}
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-600">Eindeutige Besucher</span>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {trackingData.unique_views}
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-gray-600">Erster Aufruf</span>
                </div>
                <div className="text-sm font-bold text-purple-600">
                  {formatDateTime(trackingData.first_viewed)}
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-gray-600">Letzter Aufruf</span>
                </div>
                <div className="text-sm font-bold text-orange-600">
                  {formatDateTime(trackingData.last_viewed)}
                </div>
              </Card>
            </div>

            {/* Recent Activities */}
            {/* ── Status-Verlauf: wann wurde der Status geaendert (Entwurf -> Versendet ...) ── */}
            <div>
              <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4" /> Status-Verlauf
              </h4>
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2">
                {trackingData.createdAt && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-400 font-mono text-xs w-32 shrink-0">{formatDateTime(trackingData.createdAt)}</span>
                    <Badge className="bg-slate-200 text-slate-700">Entwurf</Badge>
                    <span className="text-xs text-slate-500">Angebot erstellt</span>
                  </div>
                )}
                {(trackingData.statusHistory || []).map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="text-slate-400 font-mono text-xs w-32 shrink-0">{formatDateTime(h.at)}</span>
                    {h.from && <Badge className="bg-slate-100 text-slate-500">{h.from}</Badge>}
                    {h.from && <span className="text-slate-400">→</span>}
                    <Badge className="bg-amber-100 text-amber-700">{h.to}</Badge>
                    <span className="text-xs text-slate-400">
                      {h.source === 'kunde' ? 'durch den Kunden' : h.source === 'email-versand' ? 'per E-Mail-Versand' : h.source === 'api' ? 'per API/MCP' : 'manuell geändert'}
                    </span>
                  </div>
                ))}
                {!(trackingData.statusHistory || []).length && (
                  <p className="text-xs text-slate-400">Noch keine Statusänderungen protokolliert (Aufzeichnung ab 20.07.2026).</p>
                )}
              </div>
            </div>

            {/* ── Sitzungen: je Besuch Zeitpunkt, Verweildauer, Aktionen ── */}
            {sessions.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Sitzungen ({sessions.length})
                  <span className="text-xs font-normal text-slate-400">— ohne Cookies & IP erfasst</span>
                </h4>
                <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                  {sessions.map((sess) => (
                    <div key={sess.sessionId} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          <span className="font-medium text-slate-800">{formatDateTime(sess.startedAt)}</span>
                          <Badge className="bg-slate-200 text-slate-700 text-[10px]">
                            {sess.device === 'mobile' ? '📱 Mobil' : '💻 Desktop'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-slate-600">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          {fmtDuration(sess.durationSec)}
                        </div>
                      </div>
                      {sess.events.length > 0 ? (
                        <ul className="mt-2 space-y-1">
                          {sess.events.map((ev, i) => (
                            <li key={i} className="text-xs text-slate-600 flex items-center gap-2">
                              <span className="text-slate-400 font-mono">
                                {new Date(ev.t).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <span>{EVENT_LABELS[ev.type] || ev.type}{ev.label ? ` — ${ev.label}` : ''}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-1.5 text-xs text-slate-400">Nur angesehen, keine Interaktionen</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {trackingData.activities && trackingData.activities.length > 0 && (
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Aktivitäten
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Aktivität</TableHead>
                      <TableHead>Zeitpunkt</TableHead>
                      <TableHead>IP-Adresse</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trackingData.activities.slice(-10).reverse().map((activity, index) => (
                      <TableRow key={activity.id || index}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getActivityIcon(activity.activity_type)}
                            <Badge className={getActivityColor(activity.activity_type)}>
                              {activity.activity_type === 'view' ? 'Angesehen' :
                               activity.activity_type === 'download' ? 'Heruntergeladen' :
                               activity.activity_type === 'accept' ? 'Angenommen' :
                               activity.activity_type === 'reject' ? 'Abgelehnt' :
                               activity.activity_type === 'negotiate' ? 'Verhandelt' :
                               activity.activity_type}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDateTime(activity.timestamp)}
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {activity.ip_address}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {activity.details && Object.keys(activity.details).length > 0 
                            ? JSON.stringify(activity.details, null, 1).slice(0, 100) + '...'
                            : '-'
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}

            {/* View History */}
            {trackingData.view_history && trackingData.view_history.length > 0 && (
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Aufrufe-Historie (Letzte 20)
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Zeitpunkt</TableHead>
                      <TableHead>IP-Adresse</TableHead>
                      <TableHead>Browser</TableHead>
                      <TableHead>Session</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trackingData.view_history.slice(-20).reverse().map((view, index) => (
                      <TableRow key={view.id || index}>
                        <TableCell className="text-sm">
                          {formatDateTime(view.timestamp)}
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {view.ip_address}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                          {view.user_agent}
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {view.session_id ? view.session_id.slice(0, 8) + '...' : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}

            {/* No Data Message */}
            {trackingData.total_views === 0 && (
              <Card className="p-8 text-center">
                <Eye className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium mb-2 text-gray-600">Noch keine Aufrufe</h3>
                <p className="text-gray-500">
                  Dieses Angebot wurde noch nicht vom Kunden aufgerufen.
                </p>
              </Card>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Keine Tracking-Daten verfügbar</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteTracking;